import { useState, useEffect, useCallback } from 'react'
import {
  Agent,
  AgentDetail,
  AgentMessage,
  AgentPanelStateType,
  AgentQuickAction,
  ConversationDetail,
  DocumentContext,
} from '../types/agent.types'
import { agentService } from '../services/agent.service'
import { useOffice } from './useOffice'

/**
 * State interface for the useAgentPanel hook
 */
interface UseAgentPanelState {
  /** List of available agents */
  agents: Agent[]
  /** Currently selected agent with full details */
  currentAgent: AgentDetail | null
  /** Current UI state of the panel */
  panelState: AgentPanelStateType
  /** Whether the hook is loading data */
  isLoading: boolean
  /** Error message if an operation failed */
  error: string | null
  /** Current conversation with message history */
  conversation: ConversationDetail | null
  /** Whether the agent is currently streaming a response */
  isStreaming: boolean
}

/**
 * Return type for the useAgentPanel hook
 */
interface UseAgentPanelReturn extends UseAgentPanelState {
  /** Load available agents from the API */
  loadAgents: () => Promise<void>
  /** Select an agent by ID and load its details */
  selectAgent: (agentId: string) => Promise<void>
  /** Start a new conversation with an agent */
  startConversation: (agentId: string) => Promise<void>
  /** Send a message to the current conversation */
  sendMessage: (message: string, documentContext?: DocumentContext) => Promise<void>
  /** Execute a quick action */
  executeQuickAction: (action: AgentQuickAction) => Promise<void>
  /** Clear the current conversation */
  clearConversation: () => void
  /** Set the panel state */
  setPanelState: (state: AgentPanelStateType) => void
}

/**
 * User-friendly error messages for common API errors
 */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Handle specific error types
    if (error.message.includes('401') || error.message.includes('unauthorized')) {
      return 'Your session has expired. Please sign in again.'
    }
    if (error.message.includes('403') || error.message.includes('forbidden')) {
      return 'You do not have permission to perform this action.'
    }
    if (error.message.includes('404') || error.message.includes('not found')) {
      return 'The requested resource was not found.'
    }
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection.'
    }
    if (error.message.includes('timeout')) {
      return 'The request timed out. Please try again.'
    }
    return error.message
  }
  return 'An unexpected error occurred. Please try again.'
}

/**
 * React hook for managing the Agent Panel in Office Add-ins
 *
 * This hook provides comprehensive state management and actions for:
 * - Loading and selecting AI agents
 * - Managing conversations with agents
 * - Sending messages with document context
 * - Executing quick actions
 * - Handling loading and error states
 *
 * @example
 * ```typescript
 * function AgentPanel() {
 *   const {
 *     agents,
 *     currentAgent,
 *     conversation,
 *     isLoading,
 *     error,
 *     selectAgent,
 *     sendMessage,
 *     executeQuickAction,
 *   } = useAgentPanel();
 *
 *   return (
 *     <div>
 *       {agents.map(agent => (
 *         <button key={agent.id} onClick={() => selectAgent(agent.id)}>
 *           {agent.name}
 *         </button>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useAgentPanel = (): UseAgentPanelReturn => {
  const { context, getSelection } = useOffice()

  const [state, setState] = useState<UseAgentPanelState>({
    agents: [],
    currentAgent: null,
    panelState: 'welcome',
    isLoading: false,
    error: null,
    conversation: null,
    isStreaming: false,
  })

  /**
   * Load available agents from the API
   */
  const loadAgents = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const agents = await agentService.getAgents()
      setState(prev => ({
        ...prev,
        agents,
        isLoading: false,
      }))
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: getErrorMessage(error),
      }))
    }
  }, [])

  /**
   * Select an agent by ID and load its full details
   */
  const selectAgent = useCallback(async (agentId: string): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const agentDetail = await agentService.getAgent(agentId)
      setState(prev => ({
        ...prev,
        currentAgent: agentDetail,
        isLoading: false,
        panelState: 'conversation',
      }))
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: getErrorMessage(error),
      }))
    }
  }, [])

  /**
   * Start a new conversation with an agent
   */
  const startConversation = useCallback(async (agentId: string): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // Create a new conversation
      const newConversation = await agentService.createConversation(agentId)

      // Get the full conversation details
      const conversationDetail = await agentService.getConversation(newConversation.id)

      setState(prev => ({
        ...prev,
        conversation: conversationDetail,
        isLoading: false,
        panelState: 'conversation',
      }))
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: getErrorMessage(error),
      }))
    }
  }, [])

  /**
   * Send a message to the current conversation
   */
  const sendMessage = useCallback(
    async (message: string, documentContext?: DocumentContext): Promise<void> => {
      if (!state.conversation) {
        setState(prev => ({
          ...prev,
          error: 'No active conversation. Please start a conversation first.',
        }))
        return
      }

      // Create optimistic user message
      const userMessage: AgentMessage = {
        id: `temp-${Date.now()}`,
        agentId: state.conversation.agentId,
        role: 'user',
        content: message,
        timestamp: new Date(),
      }

      // Add user message optimistically and set streaming state
      setState(prev => ({
        ...prev,
        conversation: prev.conversation
          ? {
              ...prev.conversation,
              messages: [...prev.conversation.messages, userMessage],
            }
          : null,
        isStreaming: true,
        error: null,
      }))

      try {
        // Build document context if not provided
        let contextToSend = documentContext
        if (!contextToSend && context) {
          const selection = await getSelection()
          contextToSend = {
            documentType: String(context.host || 'word'),
            documentTitle: (context as { documentUrl?: string }).documentUrl || undefined,
            selectedText: selection?.text || undefined,
          }
        }

        // Send message to API
        const response = await agentService.sendMessage(
          state.conversation.id,
          message,
          contextToSend
        )

        // Create assistant message from response
        const assistantMessage: AgentMessage = {
          id: `msg-${Date.now()}`,
          agentId: state.conversation.agentId,
          role: 'assistant',
          content: response.content,
          timestamp: new Date(),
          actions: response.actions,
          tokenCount: {
            input: response.tokenUsage.input,
            output: response.tokenUsage.output,
          },
        }

        // Update conversation with assistant response
        setState(prev => ({
          ...prev,
          conversation: prev.conversation
            ? {
                ...prev.conversation,
                messages: [...prev.conversation.messages, assistantMessage],
                totalUsage: {
                  inputTokens:
                    prev.conversation.totalUsage.inputTokens + response.tokenUsage.input,
                  outputTokens:
                    prev.conversation.totalUsage.outputTokens + response.tokenUsage.output,
                  totalTokens:
                    prev.conversation.totalUsage.totalTokens +
                    response.tokenUsage.input +
                    response.tokenUsage.output,
                },
              }
            : null,
          isStreaming: false,
        }))
      } catch (error) {
        setState(prev => ({
          ...prev,
          isStreaming: false,
          error: getErrorMessage(error),
        }))
      }
    },
    [state.conversation, context, getSelection]
  )

  /**
   * Execute a quick action
   */
  const executeQuickAction = useCallback(
    async (action: AgentQuickAction): Promise<void> => {
      // Determine which agent to use
      const agentId = action.agentId || state.currentAgent?.id

      if (!agentId) {
        setState(prev => ({
          ...prev,
          error: 'No agent selected. Please select an agent first.',
        }))
        return
      }

      setState(prev => ({ ...prev, isLoading: true, error: null }))

      try {
        // Ensure we have a conversation
        let conversationId = state.conversation?.id

        if (!conversationId || state.conversation?.agentId !== agentId) {
          const newConversation = await agentService.createConversation(agentId)
          conversationId = newConversation.id

          const conversationDetail = await agentService.getConversation(conversationId)
          setState(prev => ({
            ...prev,
            conversation: conversationDetail,
          }))
        }

        // Get document context
        let documentContext: DocumentContext | undefined
        if (context) {
          const selection = await getSelection()
          documentContext = {
            documentType: String(context.host || 'word'),
            documentTitle: (context as { documentUrl?: string }).documentUrl || undefined,
            selectedText: selection?.text || undefined,
          }
        }

        // Execute the skill if specified, otherwise send as a message
        if (action.skillSlug) {
          const response = await agentService.executeSkill(agentId, action.skillSlug, {
            agentId,
            userMessage: action.label,
            documentContext: documentContext || { documentType: String(context?.host || 'word') },
            conversationHistory: state.conversation?.messages || [],
          })

          // Create assistant message from response
          const assistantMessage: AgentMessage = {
            id: `msg-${Date.now()}`,
            agentId,
            role: 'assistant',
            content: response.content,
            timestamp: new Date(),
            actions: response.actions,
            tokenCount: {
              input: response.tokenUsage.input,
              output: response.tokenUsage.output,
            },
          }

          setState(prev => ({
            ...prev,
            conversation: prev.conversation
              ? {
                  ...prev.conversation,
                  messages: [...prev.conversation.messages, assistantMessage],
                }
              : null,
            isLoading: false,
            panelState: 'conversation',
          }))
        } else {
          // Send as a regular message
          setState(prev => ({ ...prev, isLoading: false }))
          await sendMessage(action.label, documentContext)
        }
      } catch (error) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: getErrorMessage(error),
        }))
      }
    },
    [state.currentAgent, state.conversation, context, getSelection, sendMessage]
  )

  /**
   * Clear the current conversation
   */
  const clearConversation = useCallback((): void => {
    setState(prev => ({
      ...prev,
      conversation: null,
      panelState: 'welcome',
      error: null,
    }))
  }, [])

  /**
   * Set the panel state
   */
  const setPanelState = useCallback((panelState: AgentPanelStateType): void => {
    setState(prev => ({
      ...prev,
      panelState,
    }))
  }, [])

  // Auto-load agents on mount
  useEffect(() => {
    loadAgents()
  }, [loadAgents])

  return {
    // State
    agents: state.agents,
    currentAgent: state.currentAgent,
    panelState: state.panelState,
    isLoading: state.isLoading,
    error: state.error,
    conversation: state.conversation,
    isStreaming: state.isStreaming,
    // Actions
    loadAgents,
    selectAgent,
    startConversation,
    sendMessage,
    executeQuickAction,
    clearConversation,
    setPanelState,
  }
}

export default useAgentPanel
